/**
 * Google Tasks Conflict Service - Phase 4 Architecture
 *
 * Encapsulates conflict detection and resolution for Google Tasks sync.
 * Extracted from GoogleTasksSyncService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "@/services/core/service-registry";
import type { GoogleTaskSyncMapping, ResolveConflictRequest } from "@/types/google-tasks";
import type { Todo } from "@flowstate/types";
import { GoogleTasksApiClient } from "./google-tasks-api-client.service";
import { GoogleTasksListService } from "./google-tasks-list.service";
import { GoogleTasksTaskService } from "./google-tasks-task.service";
import { GoogleTasksSyncEngine } from "./google-tasks-sync-engine.service";

const log = logger.createNamespace("GoogleTasksConflictService");

export interface IGoogleTasksConflictService extends IService {
  getConflicts(userId: string): Promise<ServiceResponse<GoogleTaskSyncMapping[]>>;
  resolveConflict(userId: string, request: ResolveConflictRequest): Promise<ServiceResponse<void>>;
}

export class GoogleTasksConflictService implements IGoogleTasksConflictService {
  public readonly name = "GoogleTasksConflictService";
  private readonly supabase = typedSupabase;
  private apiClient: GoogleTasksApiClient;
  private listService: GoogleTasksListService;
  private taskService: GoogleTasksTaskService;
  private syncEngine: GoogleTasksSyncEngine;

  constructor(
    apiClient: GoogleTasksApiClient,
    listService: GoogleTasksListService,
    taskService: GoogleTasksTaskService,
    syncEngine: GoogleTasksSyncEngine
  ) {
    this.apiClient = apiClient;
    this.listService = listService;
    this.taskService = taskService;
    this.syncEngine = syncEngine;
  }

  async initialize(): Promise<void> {
    log.info("Google Tasks Conflict Service initialized");
  }

  async dispose(): Promise<void> {
    log.info("Google Tasks Conflict Service disposed");
  }

  async getConflicts(userId: string): Promise<ServiceResponse<GoogleTaskSyncMapping[]>> {
    const { data, error } = await this.supabase
      .from("google_task_sync_mappings")
      .select("*")
      .eq("user_id", userId)
      .eq("sync_status", "conflict");

    if (error) {
      return createErrorResponse(error.message);
    }

    return createSuccessResponse(
      (data || []).map((mapping) => ({
        ...mapping,
        sync_direction: (mapping.sync_direction ?? "bidir") as GoogleTaskSyncMapping["sync_direction"],
        sync_status: (mapping.sync_status ?? "pending") as GoogleTaskSyncMapping["sync_status"],
        last_synced_at: mapping.last_synced_at ?? undefined,
        google_last_modified: mapping.google_last_modified ?? undefined,
        flowstate_last_modified: mapping.flowstate_last_modified ?? undefined,
        google_etag: mapping.google_etag ?? undefined,
        google_position: mapping.google_position ?? undefined,
        sync_error: mapping.sync_error ?? undefined,
        conflict_resolved_at: mapping.conflict_resolved_at ?? undefined,
        conflict_resolution: mapping.conflict_resolution ?? undefined,
        created_at: mapping.created_at ?? new Date().toISOString(),
        updated_at: mapping.updated_at ?? new Date().toISOString(),
        conflict_data: (mapping.conflict_data as unknown as GoogleTaskSyncMapping["conflict_data"]) ?? undefined,
      }))
    );
  }

  async resolveConflict(
    userId: string,
    request: ResolveConflictRequest
  ): Promise<ServiceResponse<void>> {
    log.info("Resolving conflict", { mappingId: request.mappingId, resolution: request.resolution });

    const { data: mapping, error: mappingError } = await this.supabase
      .from("google_task_sync_mappings")
      .select("*")
      .eq("id", request.mappingId)
      .eq("user_id", userId)
      .single();

    if (mappingError || !mapping) {
      return createErrorResponse("Mapping not found or access denied");
    }

    if (mapping.sync_status !== "conflict") {
      return createErrorResponse("Item is not in conflict state");
    }

    try {
      switch (request.resolution) {
        case "use_google": {
          const googleTask = await this.taskService.getGoogleTaskById(
            mapping.google_task_list_id,
            mapping.google_task_id
          );
          await this.syncEngine.updateFlowStateTodoFromGoogle(
            mapping.flowstate_todo_id,
            mapping.google_task_list_id,
            googleTask
          );
          break;
        }

        case "use_flowstate": {
          const { data: todo, error: todoError } = await this.supabase
            .from("todos")
            .select("*")
            .eq("id", mapping.flowstate_todo_id)
            .single();

          if (todoError || !todo) {
            throw new Error("FlowState todo not found");
          }

          await this.syncEngine.updateGoogleTaskFromFlowState(
            mapping.google_task_id,
            todo as Todo
          );
          break;
        }

        case "merge": {
          const googleTaskMerge = await this.taskService.getGoogleTaskById(
            mapping.google_task_list_id,
            mapping.google_task_id
          );
          const { data: todoMerge, error: todoMergeError } = await this.supabase
            .from("todos")
            .select("*")
            .eq("id", mapping.flowstate_todo_id)
            .single();

          if (todoMergeError || !todoMerge) {
            throw new Error("FlowState todo not found");
          }

          const mergedTodo: Todo = {
            ...(todoMerge as Todo),
            description: `${todoMerge.description ?? ""}\n\n--- Google Tasks ---\n${googleTaskMerge.notes ?? ""}`.trim(),
          };

          await this.syncEngine.updateFlowStateTodoFromGoogle(
            mapping.flowstate_todo_id,
            mapping.google_task_list_id,
            googleTaskMerge
          );
          await this.syncEngine.updateGoogleTaskFromFlowState(
            mapping.google_task_id,
            mergedTodo
          );
          break;
        }

        default:
          return createErrorResponse("Invalid resolution strategy");
      }

      await this.supabase
        .from("google_task_sync_mappings")
        .update({
          sync_status: "synced",
          last_synced_at: new Date().toISOString(),
          conflict_resolved_at: new Date().toISOString(),
          conflict_resolution: request.resolution,
        })
        .eq("id", request.mappingId);

      log.info("Conflict resolved", { mappingId: request.mappingId, resolution: request.resolution });

      await this.apiClient.logGoogleTasksActivity(
        userId,
        mapping.google_task_list_id,
        mapping.google_task_id,
        "conflict_resolved",
        "gtasks.conflict_resolved",
        {
          mapping_id: request.mappingId,
          resolution: request.resolution,
          conflict_resolution: request.resolution,
        }
      );

      return createSuccessResponse(undefined);
    } catch (error) {
      log.error("Conflict resolution failed", { error, mappingId: request.mappingId });
      return createErrorResponse(
        error instanceof Error ? error.message : "Conflict resolution failed"
      );
    }
  }
}
